import React from 'react';

const MapSearchPanel = ({
  aiSearchQuery, setAiSearchQuery,
  onAISearch, onAutoCategorize, onDatabaseSearch, onSearchEngineAccess,
  aiSearchLoading, dbSearchLoading, seaLoading,
  aiSearchMode, setAiSearchMode,
  dbSearchMode, setDbSearchMode,
  searchPreferences, toggleSearchPreference
}) => {
  return (
    <div style={{ 
      background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.15), rgba(253, 230, 138, 0.15))',
      borderRadius: 12,
      padding: 15,
      marginBottom: 15,
      border: '1px solid rgba(252, 211, 77, 0.3)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input
          type="text"
          value={aiSearchQuery}
          onChange={(e) => setAiSearchQuery(e.target.value)}
          placeholder="Enter search query for AI-powered search..."
          style={{
            flex: 1,
            minWidth: 200,
            padding: '10px 15px',
            background: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid rgba(252, 211, 77, 0.4)',
            borderRadius: 8,
            color: '#0F172A'
          }}
          onKeyPress={(e) => e.key === 'Enter' && onAISearch()}
          data-testid="map-ai-search-input"
        />
        <button
          onClick={onAutoCategorize}
          disabled={aiSearchLoading || !aiSearchQuery.trim()}
          className="btn"
          style={{
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: '#0F172A',
            padding: '10px 15px',
            opacity: (!aiSearchQuery.trim() || aiSearchLoading) ? 0.5 : 1
          }}
          data-testid="map-auto-categorize-btn"
        >
          {aiSearchLoading ? '\u23F3' : '\uD83C\uDFAF'} Auto-Categorize
        </button>
        <button
          onClick={onAISearch}
          disabled={aiSearchLoading || !aiSearchQuery.trim()}
          className="btn"
          style={{
            background: 'linear-gradient(135deg, #FDE047, #FCD34D)',
            color: '#0F172A',
            padding: '10px 15px',
            opacity: (!aiSearchQuery.trim() || aiSearchLoading) ? 0.5 : 1
          }}
          data-testid="map-ai-search-btn"
        >
          {aiSearchLoading ? 'Searching...' : 'AI Search'}
        </button>
        <select
          value={aiSearchMode}
          onChange={(e) => setAiSearchMode(e.target.value)}
          style={{
            background: 'rgba(252, 211, 77, 0.3)',
            border: '1px solid rgba(252, 211, 77, 0.4)',
            color: '#047857',
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: '0.85rem'
          }}
        >
          <option value="comprehensive">Comprehensive</option>
          <option value="news">News</option>
          <option value="research">Research</option>
        </select>
        <button
          onClick={onDatabaseSearch}
          disabled={dbSearchLoading || !aiSearchQuery.trim()}
          className="btn"
          style={{
            background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
            color: '#0F172A',
            padding: '10px 15px',
            opacity: (!aiSearchQuery.trim() || dbSearchLoading) ? 0.5 : 1
          }}
          data-testid="map-database-search-btn"
        >
          {dbSearchLoading ? 'Searching...' : 'DB Search'}
        </button>
        <select
          value={dbSearchMode}
          onChange={(e) => setDbSearchMode(e.target.value)}
          style={{
            background: 'rgba(6, 182, 212, 0.3)',
            border: '1px solid rgba(6, 182, 212, 0.4)',
            color: '#22d3ee',
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: '0.85rem'
          }}
        >
          <option value="smart">Smart</option>
          <option value="exact">Exact</option>
          <option value="fuzzy">Fuzzy</option>
        </select>
        {/* Search Engine Access Button */}
        <button
          onClick={onSearchEngineAccess}
          disabled={seaLoading || !aiSearchQuery.trim()}
          className="btn"
          style={{
            background: 'linear-gradient(135deg, #22d3ee, #0891b2)',
            color: '#0F172A',
            padding: '10px 15px',
            opacity: (!aiSearchQuery.trim() || seaLoading) ? 0.5 : 1
          }}
          data-testid="map-search-engine-access-btn"
        >
          {seaLoading ? 'Searching...' : 'Search Engines'}
        </button>
      </div>
      
      {/* Search Preference Checkboxes for instant map updates */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 10, 
        marginTop: 10,
        padding: '10px',
        background: 'rgba(253, 224, 71, 0.1)',
        borderRadius: 8,
        border: '1px solid rgba(253, 224, 71, 0.2)'
      }}>
        <span style={{ fontSize: '0.75rem', color: '#a1a1aa', fontWeight: 600 }}>
          Search Preferences:
        </span>
        {[
          { id: 'fuzzySearch', label: 'Fuzzy Match', icon: '\uD83D\uDD0D' },
          { id: 'narrowSearch', label: 'Narrow Search', icon: '\uD83C\uDFAF' },
          { id: 'highQuality', label: 'High Quality', icon: '\u2B50' },
          { id: 'favorSchematics', label: 'Favor Schematics & Diagrams', icon: '\uD83D\uDCCA' }
        ].map(pref => (
          <label
            key={pref.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
              borderRadius: 6,
              cursor: 'pointer',
              background: searchPreferences[pref.id] 
                ? 'rgba(253, 224, 71, 0.3)' 
                : 'rgba(255, 255, 255, 0.85)',
              border: searchPreferences[pref.id]
                ? '1px solid rgba(253, 224, 71, 0.5)'
                : '1px solid rgba(255,255,255,0.1)',
              fontSize: '0.75rem',
              color: searchPreferences[pref.id] ? '#047857' : '#9ca3af'
            }}
          >
            <input
              type="checkbox"
              checked={searchPreferences[pref.id]}
              onChange={() => toggleSearchPreference(pref.id)}
              style={{ accentColor: '#FDE047', width: 12, height: 12 }}
            />
            <span>{pref.icon}</span>
            <span>{pref.label}</span>
          </label>
        ))}
      </div>
      
      <p style={{ color: '#a1a1aa', fontSize: '0.75rem', marginTop: 8, marginBottom: 0 }}>
        <strong>AI Search:</strong> Google, Bing, DuckDuckGo, Brave with GPT keyword expansion. <strong>DB Search:</strong> Search your already categorized results. <strong>Search Engines:</strong> Direct access to all search engines.
      </p>
    </div>
  );
};

export default MapSearchPanel;
